import { createSlice } from "@reduxjs/toolkit";
import {
  fetchCustomersList,
  fetchSalesVouchers,
  fetchSalesReturnVoucher,
  fetchSalesVoucherDetails,
  createSalesReturn,
  fetchSalesReturnAccounts,
  fetchDiscountAccounts,
  fetchTaxAccounts,
  fetchTransportAccounts,
  fetchAvailableSerials,
  fetchSalesReturnWithDetails
} from "./salesReturnThunks";
import { currentDateTime } from "../../lib/functions";

const initialState = {
  customersList: [],
  salesVouchersList: [],
  salesReturnVoucher: null,
  salesReturnSuccess: null,
  selectedVoucherDetails: null,
  isEdit: false,
  selectedRecord: null,
  loading: false,
  error: null,

  // New account dropdowns
  salesReturnAccounts: [],
  taxAccounts: [],
  discountAccounts: [],
  transportAccounts: [],

  // Serial tracking per item
  selectedSerials: {},

  // 🆕 Form data
  formData: {
    taxMode: "",
    customer: null,
    selectedSaleInvoice: null,
    itemsList: [],
    payServiceCart: [],
    selectedSalesReturnAcc: null,
    selectedItem: null,
    created_date: currentDateTime(),
    tags: [],
    footerValues: {
      paidAmount: 0,
      subTotal: 0,
      serviceCharge: 0,
      totalAmount: 0,
      payCart: [],
      otherServiceTransactions: [],
    },

    parent_sscc_code: "",
    is_tatmeen_product: false,
  },
};

const salesReturnSlice = createSlice({
  name: "salesReturn",
  initialState,
  reducers: {
    resetSalesReturnState: () => ({ ...initialState }),
    resetSalesReturnStatus: (state) => {
      state.salesReturnSuccess = null;
    },
    resetVouchersList: (state) => {
      state.salesVouchersList = [];
    },
    updateSelectedSerials: (state, action) => {
      const { itemId, serials } = action.payload;
      state.selectedSerials[itemId] = serials;
    },
    clearSelectedSerials: (state) => {
      state.selectedSerials = {};
    },

    setSelectedSalesReturnRecord: (state, action) => {
      state.selectedRecord = action.payload;
    },
    setIsEditSalesReturn: (state, action) => {
      state.isEdit = action.payload;
    },

    // 🆕 Form field update
    setFormField: (state, action) => {
      const { field, value } = action.payload;

      // ✅ Merge for nested footerValues instead of full replace
      if (field === "footerValues") {
        state.formData.footerValues = {
          ...state.formData.footerValues,
          ...value,
        };
      } else {
        state.formData[field] = value;
      }
    },

    resetFormData: (state) => {
      state.formData = {
        taxMode: "",
        customer: null,
        selectedSaleInvoice: null,
        itemsList: [],
        selectedSalesReturnAcc: null,
        selectedItem: null,
        created_date: currentDateTime(),
        tags: [],
        footerValues: {
          paidAmount: 0,
          subTotal: 0,
          serviceCharge: 0,
          totalAmount: 0,
          payCart: [],
        },
        parent_sscc_code: "",
        is_tatmeen_product: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomersList.fulfilled, (state, action) => {
        state.customersList = action.payload;
      })
      .addCase(fetchSalesVouchers.fulfilled, (state, action) => {
        state.salesVouchersList = action.payload;
      })
      .addCase(fetchSalesReturnVoucher.fulfilled, (state, action) => {
        state.salesReturnVoucher = action.payload;
        state.formData.sale_r_voucher_no = action.payload;
      })
      .addCase(fetchSalesVoucherDetails.fulfilled, (state, action) => {
        const { salesDetails, returnedItems, tatmeenData } = action.payload || {};
        state.selectedVoucherDetails = action.payload;
        if (tatmeenData) {
          // state.formData.parent_sscc_code = tatmeenData.sscc_code || "";
          state.formData.is_tatmeen_product = tatmeenData.is_tatmeen_product || false;
        }

      })
      .addCase(createSalesReturn.fulfilled, (state, action) => {
        state.salesReturnSuccess = action.payload;
      })
      .addCase(fetchSalesReturnAccounts.fulfilled, (state, action) => {
        state.salesReturnAccounts = action.payload;
      })
      .addCase(fetchTaxAccounts.fulfilled, (state, action) => {
        state.taxAccounts = action.payload;
      })
      .addCase(fetchDiscountAccounts.fulfilled, (state, action) => {
        state.discountAccounts = action.payload;
      })
      .addCase(fetchTransportAccounts.fulfilled, (state, action) => {
        state.transportAccounts = action.payload;
      })
      .addCase(fetchAvailableSerials.fulfilled, (state, action) => {
        const { itemId, serials } = action.payload;
        state.selectedSerials[itemId] = serials;
      })
      .addCase(fetchSalesReturnWithDetails.pending, (state) => {
        state.isEdit = true;
        state.selectedRecord = null;
      })
      .addCase(fetchSalesReturnWithDetails.fulfilled, (state, { payload }) => {

        state.isEdit = true;

        const { header, items } = payload;
        if (!header) return;

        // -------- MASTER --------
        state.sale_r_voucher_no = header.sale_r_voucher_no;
        state.formData.sale_r_voucher_no = header.sale_r_voucher_no;
        state.formData.customer = header.customer;
        state.formData.selectedSaleInvoice = header.selectedSaleInvoice;
        state.formData.taxMode = header.taxMode;
        state.formData.created_date = header.created_date;
        state.formData.tags = header.tags || [];
        state.formData.selectedSalesReturnAcc = header.salesReturnAcc;

        // accounts
        state.formData.discountAcc = header.discountAcc;
        state.formData.taxAcc = header.taxAcc;
        state.formData.transportAcc = header.transportAcc;

        // -------- ITEMS --------
        // state.formData.itemsList = items;
        state.formData.itemsList = items.map(item => ({
          ...item,
          is_tatmeen_required: item.is_tatmeen_required === true ||
            item.is_tatmeen_required === 1 ||
            item.is_tatmeen_required === "1",
        }));

        // -------- FOOTER --------
        state.formData.footerValues = {
          ...state.formData.footerValues,
          ...header.footerValues
        };

        state.formData.parent_sscc_code =
          header.parent_sscc_code || "";

        state.formData.is_tatmeen_product =
          header.is_tatmeen_product === 1;
      })


      .addCase(fetchSalesReturnWithDetails.rejected, (state) => {
        state.isEdit = false;
        state.selectedRecord = null;
      });

  },
});

export const {
  resetSalesReturnStatus,
  resetVouchersList,
  updateSelectedSerials,
  clearSelectedSerials,
  setSelectedSalesReturnRecord,
  setIsEditSalesReturn,
  setFormField,
  resetFormData,
  resetSalesReturnState,
} = salesReturnSlice.actions;

export default salesReturnSlice.reducer;
